import { motion } from 'framer-motion'
import { 
  Star, 
  TrendingUp, 
  TrendingDown, 
  Lightbulb,
  ChevronDown,
  ChevronUp,
  BarChart3
} from 'lucide-react'
import { useState } from 'react'

interface CritiquePanelProps {
  score: number | null
  feedback: Record<string, unknown> | null
  compact?: boolean
}

const DIMENSION_LABELS: Record<string, { label: string; icon: string }> = {
  adherence_score: { label: 'Adherencia a marca', icon: '🎯' },
  clarity_score: { label: 'Claridad', icon: '💡' },
  value_score: { label: 'Valor', icon: '💎' },
  engagement_score: { label: 'Engagement', icon: '🔥' },
  cta_score: { label: 'CTA', icon: '👆' },
  product_alignment_score: { label: 'Alineación producto', icon: '🎪' },
  overall_score: { label: 'General', icon: '⭐' },
}

function ScoreGauge({ score, size = 'lg' }: { score: number; size?: 'sm' | 'lg' }) {
  const radius = size === 'lg' ? 45 : 28
  const stroke = size === 'lg' ? 8 : 5
  const center = radius + stroke
  const svgSize = (radius + stroke) * 2
  const circumference = 2 * Math.PI * radius
  const normalizedScore = Math.min(Math.max(score, 0), 10)
  const progress = (normalizedScore / 10) * circumference

  const getColor = (s: number) => {
    if (s >= 8) return { stroke: '#10b981', text: 'text-emerald-600', bg: 'bg-emerald-50' }
    if (s >= 6) return { stroke: '#f59e0b', text: 'text-amber-600', bg: 'bg-amber-50' }
    return { stroke: '#ef4444', text: 'text-red-600', bg: 'bg-red-50' }
  }

  const color = getColor(normalizedScore)

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={svgSize} height={svgSize} className="-rotate-90">
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth={stroke}
        />
        {/* Progress circle */}
        <motion.circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color.stroke}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: circumference - progress }}
          transition={{ duration: 1, ease: 'easeOut' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <span className={`${size === 'lg' ? 'text-2xl' : 'text-base'} font-bold ${color.text}`}>
            {normalizedScore.toFixed(1)}
          </span>
          {size === 'lg' && (
            <span className="block text-xs text-slate-400">/10</span>
          )}
        </div>
      </div>
    </div>
  )
}

function ScoreBar({ label, icon, score }: { label: string; icon: string; score: number }) {
  const normalizedScore = Math.min(Math.max(score, 0), 10)
  const percentage = (normalizedScore / 10) * 100

  const getBarColor = (s: number) => {
    if (s >= 8) return 'bg-emerald-500'
    if (s >= 6) return 'bg-amber-500'
    return 'bg-red-500'
  }

  return (
    <div className="flex items-center gap-3">
      <span className="text-sm w-5 text-center">{icon}</span>
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-slate-600">{label}</span>
          <span className="text-xs font-medium text-slate-700">{normalizedScore.toFixed(1)}</span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <motion.div
            className={`h-full rounded-full ${getBarColor(normalizedScore)}`}
            initial={{ width: 0 }}
            animate={{ width: `${percentage}%` }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          />
        </div>
      </div>
    </div>
  )
}

export function CritiquePanel({ score, feedback, compact = false }: CritiquePanelProps) {
  const [isExpanded, setIsExpanded] = useState(!compact)

  if (score === null && !feedback) {
    return (
      <div className="text-center py-6 text-slate-400">
        <BarChart3 className="w-8 h-8 mx-auto mb-2" />
        <p className="text-sm">Sin evaluación disponible</p>
      </div>
    )
  }

  // Extract structured data from feedback
  const scores: { key: string; label: string; icon: string; value: number }[] = []
  const strengths: string[] = []
  const weaknesses: string[] = []
  const suggestions: string[] = []

  if (feedback) {
    Object.entries(feedback).forEach(([key, value]) => {
      if (key === 'approved' || key === 'requires_revision') return

      if (key in DIMENSION_LABELS && typeof value === 'number') {
        scores.push({
          key,
          label: DIMENSION_LABELS[key].label,
          icon: DIMENSION_LABELS[key].icon,
          value,
        })
      } else if (key === 'strengths' && Array.isArray(value)) {
        strengths.push(...value.map(String))
      } else if (key === 'weaknesses' && Array.isArray(value)) {
        weaknesses.push(...value.map(String))
      } else if (key === 'suggestions' && Array.isArray(value)) {
        suggestions.push(...value.map(String))
      } else if (key.endsWith('_score') && typeof value === 'number') {
        // Handle dynamic score keys not in DIMENSION_LABELS
        const label = key.replace(/_score$/, '').replace(/_/g, ' ')
        scores.push({
          key,
          label: label.charAt(0).toUpperCase() + label.slice(1),
          icon: '📊',
          value,
        })
      }
    })
  }

  if (compact) {
    return (
      <div className="space-y-3">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center justify-between w-full text-left"
        >
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-medium text-slate-700">Evaluación AI</span>
            {score !== null && (
              <span className="text-sm font-bold text-slate-800">{score.toFixed(1)}/10</span>
            )}
          </div>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
        </button>

        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="space-y-3"
          >
            {scores.length > 0 && (
              <div className="space-y-2">
                {scores
                  .filter((s) => s.key !== 'overall_score')
                  .map((s) => (
                    <ScoreBar key={s.key} label={s.label} icon={s.icon} score={s.value} />
                  ))}
              </div>
            )}
          </motion.div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Main score gauge */}
      {score !== null && (
        <div className="flex flex-col items-center">
          <ScoreGauge score={score} size="lg" />
          <p className="mt-2 text-sm font-medium text-slate-600">Score General</p>
        </div>
      )}

      {/* Dimension breakdown */}
      {scores.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Desglose
          </h4>
          {scores
            .filter((s) => s.key !== 'overall_score')
            .sort((a, b) => b.value - a.value)
            .map((s) => (
              <ScoreBar key={s.key} label={s.label} icon={s.icon} score={s.value} />
            ))}
        </div>
      )}

      {/* Strengths */}
      {strengths.length > 0 && (
        <div>
          <h4 className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
            Fortalezas
          </h4>
          <div className="space-y-1.5">
            {strengths.map((s, i) => (
              <div
                key={i}
                className="flex items-start gap-2 text-xs text-slate-600 bg-emerald-50 rounded-lg px-3 py-2"
              >
                <span className="text-emerald-500 mt-0.5">+</span>
                <span>{s}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Weaknesses */}
      {weaknesses.length > 0 && (
        <div>
          <h4 className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            <TrendingDown className="w-3.5 h-3.5 text-red-500" />
            Debilidades
          </h4>
          <div className="space-y-1.5">
            {weaknesses.map((w, i) => (
              <div
                key={i}
                className="flex items-start gap-2 text-xs text-slate-600 bg-red-50 rounded-lg px-3 py-2"
              >
                <span className="text-red-500 mt-0.5">-</span>
                <span>{w}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div>
          <h4 className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
            <Lightbulb className="w-3.5 h-3.5 text-amber-500" />
            Sugerencias
          </h4>
          <div className="space-y-1.5">
            {suggestions.map((s, i) => (
              <div
                key={i}
                className="flex items-start gap-2 text-xs text-slate-600 bg-amber-50 rounded-lg px-3 py-2"
              >
                <Lightbulb className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                <span>{s}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
